import { useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';
import DateSpanRow from './DateSpanRow';
import AddSpanButton from './AddSpanButton';
import StepHeading from './StepHeading';
import { ConfigurationFormData } from './formData';

interface Props {
  disabled?: boolean;
}

export default function BlackoutDatesField({ disabled = false }: Props) {
  const intl = useIntl();
  const { control, register, formState } = useFormContext<ConfigurationFormData>();
  const { fields, append, remove } = useFieldArray({ control, name: 'blackouts' });
  const [addedIndex, setAddedIndex] = useState<number | null>(null);
  const [announcement, setAnnouncement] = useState('');

  const addBlackout = () => {
    append({ from: '', to: '' });
    setAddedIndex(fields.length);
    setAnnouncement(intl.formatMessage({ id: 'accounts.configForm.blackoutAdded' }));
  };

  const removeBlackout = (index: number) => {
    remove(index);
    setAddedIndex(null);
    setAnnouncement(intl.formatMessage({ id: 'accounts.configForm.blackoutRemoved' }));
  };

  const required = intl.formatMessage({ id: 'accounts.configForm.dateRequired' });
  const hasRows = fields.length > 0;
  const addButton = (
    <AddSpanButton onClick={addBlackout} disabled={disabled} labelId="accounts.configForm.addBlackout" />
  );

  return (
    <fieldset>
      {hasRows && <StepHeading labelId="accounts.configForm.blackoutsHeading" />}

      {hasRows ? (
        <div className="mt-2 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="divide-y divide-slate-100">
            {fields.map((field, index) => (
              <DateSpanRow
                key={field.id}
                idPrefix={`blackout-${index}`}
                from={register(`blackouts.${index}.from`, { required })}
                to={register(`blackouts.${index}.to`, {
                  required,
                  validate: (value, values) =>
                    value >= values.blackouts[index].from ||
                    intl.formatMessage({ id: 'accounts.configForm.maxDateError' }),
                })}
                fromError={formState.errors.blackouts?.[index]?.from}
                toError={formState.errors.blackouts?.[index]?.to}
                autoFocus={index === addedIndex}
                disabled={disabled}
                removeLabel={intl.formatMessage({ id: 'accounts.configForm.removeBlackout' })}
                onRemove={() => removeBlackout(index)}
              />
            ))}
          </div>

          <div className="border-t border-slate-100 bg-slate-50/60 px-4 py-2">{addButton}</div>
        </div>
      ) : (
        <div className="mt-2 rounded-xl border border-dashed border-slate-300 bg-white px-4 py-2 shadow-sm">
          {addButton}
        </div>
      )}

      <p role="status" aria-live="polite" className="sr-only">
        {announcement}
      </p>
    </fieldset>
  );
}
